import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button, DataTable, FilterBar, Pagination, Pill, SelectField, TextField,
  fmtBRL, type Column,
} from "@atlas/ui/web";
import type {
  AdminObrigacaoLegada,
} from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { useDebouncedValue } from "../../lib/use-debounce";
import { Corta, Modal } from "../prefeitura/_ui";
import { PRODUTO_LABEL } from "./motor-rubricas-labels";

/**
 * As três abas de LEITURA do Motor de rubricas.
 *
 * Vivem num arquivo próprio porque `motor-rubricas.tsx` já carrega o catálogo, as
 * pendências e dois modais de escrita — juntar tudo repetiria o problema da tela
 * antiga, que tinha 573 linhas e três assuntos disputando o mesmo espaço.
 *
 * Nenhuma delas é motor: tudo o que mostram já está persistido e é derivado na
 * hora. Não há agregado guardado, e não deve haver — um contador gravado no fim
 * da importação passaria a mentir no dia em que alguém reenviasse o retorno.
 */

const MESES = ["", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function fmtCompetencia(c: string | null | undefined): string {
  if (!c || !/^\d{6}$/.test(c)) return "—";
  return `${MESES[Number(c.slice(4, 6))] ?? "?"}/${c.slice(0, 4)}`;
}

function competenciaAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * Rótulo e cor de cada resultado de desconto.
 *
 * `suspensa_sem_folha` NÃO é falha, e a cor precisa dizer isso: a prefeitura não
 * deixou de descontar, não havia salário. Pintá-la de vermelho junto com `falha`
 * faria alguém cobrar explicação de quem não deve nenhuma. `suspensa_por_teto`
 * (#277) também não: quem tirou o desconto do movimento foi a Atlas.
 */
const RESULTADO: Record<string, { label: string; variant: "aceita" | "pendente" | "rejeitada" | "expirado" | "averbado" }> = {
  aplicada: { label: "Descontado", variant: "aceita" },
  parcial: { label: "Desconto parcial", variant: "pendente" },
  falha: { label: "Não descontado", variant: "rejeitada" },
  interrompida_desligamento: { label: "Interrompido — desligamento", variant: "expirado" },
  suspensa_sem_folha: { label: "Sem folha no mês", variant: "expirado" },
  suspensa_por_teto: { label: "Suspenso pelo teto global", variant: "expirado" },
  recebida: { label: "Instruído", variant: "averbado" },
};

const resultadoDe = (r: string | null | undefined) =>
  (r && RESULTADO[r]) || { label: r || "—", variant: "pendente" as const };

const GRADE_FILTROS: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 10,
};

// ============================================================ Obrigações

/**
 * As dívidas que o catálogo cunhou, e o quanto delas ainda é provisório.
 *
 * A obrigação legada NÃO é um contrato confirmado: o banco foi derivado do
 * vínculo da rubrica porque a carteira oficial não chegou. A tela diz isso em
 * duas alturas — no cabeçalho, com o contador; e em cada linha, com a marca.
 */
export function ObrigacoesLegadas({ folhaId }: { folhaId: number }) {
  const [busca, setBusca] = useState("");
  const [banco, setBanco] = useState("");
  const [verba, setVerba] = useState("");
  const [vinculo, setVinculo] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);
  const [vendo, setVendo] = useState<AdminObrigacaoLegada | null>(null);

  // Sem debounce cada tecla e uma queryKey nova, ou seja um request.
  const buscaDebounced = useDebouncedValue(busca);

  // Os quatro filtros sobem para a rota junto com o recorte. Subir o recorte
  // sozinho faria a busca procurar apenas dentro da pagina.
  const q = useQuery({
    queryKey: ["admin", "rubricas", "obrigacoes", folhaId, buscaDebounced, banco, verba, vinculo, pagina, porPagina],
    queryFn: () => atlas.admin.rubricas.obrigacoes(folhaId, {
      q: buscaDebounced || undefined,
      banco_id: banco ? Number(banco) : undefined,
      verba: verba || undefined,
      vinculo: (vinculo || undefined) as "provisorio" | "oficial" | undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });

  const filtrar = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPagina(1); };

  // FACETAS VEM DA API desde a 007: elas descrevem a FOLHA inteira, e a tela
  // so tem a pagina. Deriva-las de `q.data.obrigacoes` faria os selects
  // encolherem a cada filtro — e o operador nao teria como voltar.
  const facetas = q.data?.facetas ?? { bancos: [], verbas: [] };
  const bancos: [number, string][] = facetas.bancos.map((b) => [b.id, b.nome]);
  const verbas = facetas.verbas;

  // A pagina JA vem filtrada e ordenada por matricula.
  const daPagina = q.data?.obrigacoes ?? [];
  const totalDoRecorte = q.data?.total ?? 0;
  const totalDaFolha = q.data?.totalBase ?? 0;

  const columns: Column<AdminObrigacaoLegada>[] = [
    {
      key: "servidor",
      header: "Servidor",
      width: 210,
      render: (o) => (
        <div style={{ display: "grid", gap: 2 }}>
          <Corta largura={198} titulo={o.nome} estilo={{ fontSize: 13 }}>{o.nome || "—"}</Corta>
          <span style={{ fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>
            {o.matricula} · {o.cpfMasked}
          </span>
        </div>
      ),
    },
    {
      key: "banco",
      header: "Consignatária derivada",
      width: 180,
      render: (o) => (
        <div style={{ display: "grid", gap: 2 }}>
          <Corta largura={168} titulo={o.bancoNome} estilo={{ fontSize: 12 }}>{o.bancoNome}</Corta>
          {/* A MARCA. Sem ela a tela apresentaria uma dedução como fato. */}
          {o.provisorio ? (
            <span
              style={{ fontSize: 11, color: "var(--gold-500)" }}
              title="A consignatária foi derivada do vínculo da rubrica. Nenhum contrato oficial foi informado ainda."
            >
              por rubrica · sem contrato oficial
            </span>
          ) : (
            <span style={{ fontSize: 11, color: "var(--emerald-500)" }} title={`Contrato oficial ${o.numeroContratoBanco}`}>
              contrato {o.numeroContratoBanco}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "rubrica",
      header: "Rubrica / produto",
      width: 170,
      render: (o) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 600 }}>{o.codigoVerba || "—"}</span>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
            {o.tipoMargem ? PRODUTO_LABEL[o.tipoMargem] : "sem produto"}
          </span>
        </div>
      ),
    },
    {
      key: "parcela",
      header: "Parcela",
      width: 130,
      align: "right",
      render: (o) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 13, fontFamily: "var(--font-mono)" }}>{fmtBRL(o.valorParcela)}</span>
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
            {o.parcelasPagas}/{o.totalParcelas} · faltam {o.parcelasRestantes}
          </span>
        </div>
      ),
    },
    {
      key: "ultimoFato",
      header: "Última folha",
      width: 170,
      render: (o) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 12 }}>{fmtCompetencia(o.competenciaUltimoFato)}</span>
          {o.resultadoUltimoFato ? (
            <Pill variant={resultadoDe(o.resultadoUltimoFato).variant}>
              {resultadoDe(o.resultadoUltimoFato).label}
            </Pill>
          ) : (
            <span style={{ fontSize: 11, opacity: 0.5 }}>sem fato registrado</span>
          )}
        </div>
      ),
    },
    { key: "situacao", header: "Situação", width: 140, render: (o) => <span style={{ fontSize: 12 }}>{o.situacao}</span> },
    {
      key: "acoes",
      header: "Ações",
      width: 110,
      render: (o) => (
        <Button size="sm" variant="ghost" title="Histórico mensal desta obrigação" onClick={() => setVendo(o)}>
          ⧖ Histórico
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ padding: 12, borderRadius: 10, fontSize: 13, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
        <strong>Identificação atual: vínculo por rubrica.</strong>{" "}
        {q.data ? (
          <>
            {q.data.provisorias} de {totalDaFolha} obrigação(ões) ainda sem contrato oficial informado
            {q.data.comContratoOficial > 0 ? `, ${q.data.comContratoOficial} já com número da consignatária` : ""}.
          </>
        ) : "Carregando…"}
        <div style={{ marginTop: 4, color: "var(--text-muted)", fontSize: 12 }}>
          Enquanto a carteira oficial das consignatárias não chega, a consignatária de uma dívida legada é derivada do vínculo da rubrica.
          É uma dedução estruturada — não um contrato confirmado.
        </div>
      </div>

      <FilterBar
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        onReset={() => { setBusca(""); setBanco(""); setVerba(""); setVinculo(""); setPagina(1); }}
      >
        <div style={GRADE_FILTROS}>
          <SelectField
            label="Consignatária derivada" value={banco} onChange={(e) => filtrar(setBanco)(e.target.value)}
            options={[{ value: "", label: "— TODOS —" }, ...bancos.map(([id, nome]) => ({ value: String(id), label: nome }))]}
          />
          <SelectField
            label="Rubrica" value={verba} onChange={(e) => filtrar(setVerba)(e.target.value)}
            options={[{ value: "", label: "— TODAS —" }, ...verbas.map((v) => ({ value: v, label: v }))]}
          />
          <SelectField
            label="Identificação" value={vinculo} onChange={(e) => filtrar(setVinculo)(e.target.value)}
            options={[
              { value: "", label: "— TODAS —" },
              { value: "provisorio", label: "Por rubrica (provisória)" },
              { value: "oficial", label: "Com contrato oficial" },
            ]}
          />
        </div>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina}
        rowKey={(o) => o.adf}
        loading={q.isLoading}
        emptyState={q.isError
          ? "Não foi possível carregar as obrigações."
          : totalDaFolha === 0
            ? "Nenhuma obrigação legada nesta folha. Elas nascem na carga inicial e na conciliação, quando uma rubrica classificada encontra um desconto que a Atlas não instruiu."
            : "Nenhuma obrigação encontrada com esses filtros."}
      />

      <Pagination
        total={totalDoRecorte}
        pagina={pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="obrigações"
      />

      {vendo ? <ModalHistoricoObrigacao obrigacao={vendo} onClose={() => setVendo(null)} /> : null}
    </div>
  );
}

/**
 * O histórico mensal — um fato por competência, só leitura.
 *
 * Não há formulário de edição por decisão: o que a folha descontou em março é um
 * fato daquele mês, e corrigi-lo aqui apagaria a divergência em vez de
 * resolvê-la. Correção se faz reenviando o retorno da competência.
 */
function ModalHistoricoObrigacao({ obrigacao, onClose }: { obrigacao: AdminObrigacaoLegada; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["admin", "rubricas", "obrigacao-historico", obrigacao.adf],
    queryFn: () => atlas.admin.rubricas.historicoObrigacao(obrigacao.adf),
  });

  const fatos = [...(q.data?.historico ?? [])].sort((a, b) => b.competencia.localeCompare(a.competencia));

  return (
    <Modal title={`Histórico · ${obrigacao.nome}`} onClose={onClose} maxWidth={700}>
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gap: 4, fontSize: 12, color: "var(--text-muted)" }}>
          <span>
            ADF <b style={{ fontFamily: "var(--font-mono)" }}>{obrigacao.adf}</b> · rubrica{" "}
            <b style={{ fontFamily: "var(--font-mono)" }}>{obrigacao.codigoVerba}</b> · {obrigacao.bancoNome}
          </span>
          <span>
            {obrigacao.provisorio
              ? "Identificação atual: vínculo por rubrica · Contrato oficial: ainda não informado"
              : `Contrato oficial: ${obrigacao.numeroContratoBanco}`}
          </span>
          {obrigacao.origemArquivo ? (
            <span>
              Veio do arquivo da competência {fmtCompetencia(obrigacao.origemArquivo.competencia)}, linha{" "}
              {obrigacao.origemArquivo.linha}.
            </span>
          ) : null}
        </div>

        <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
          Um fato por competência, como a folha reportou. <strong>Somente leitura</strong> — valor histórico não se
          edita a mão: correção se faz reenviando o retorno daquele mês.
        </p>

        {q.isLoading ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>Carregando…</div>
        ) : fatos.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
            Nenhum fato registrado ainda — esta dívida entrou na base mas nenhuma folha reportou desconto dela.
          </div>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {fatos.map((f) => {
              const r = resultadoDe(f.resultado);
              return (
                <div
                  key={f.id}
                  style={{ padding: 10, borderRadius: 8, background: "var(--bg-elev-2)", border: "1px solid var(--border)", display: "grid", gap: 4 }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <strong style={{ fontSize: 13 }}>{fmtCompetencia(f.competencia)}</strong>
                    <Pill variant={r.variant}>{r.label}</Pill>
                  </div>
                  <div style={{ fontSize: 12, display: "flex", gap: 14, flexWrap: "wrap" }}>
                    <span>instruído <b>{fmtBRL(f.valorInstruido)}</b></span>
                    <span>descontado <b>{fmtBRL(f.valorEfetivo)}</b></span>
                    {f.residuo > 0 ? (
                      <span style={{ color: "var(--gold-500)" }} title="O que faltou para fechar a parcela — a consignatária cobra por fora.">
                        resíduo <b>{fmtBRL(f.residuo)}</b>
                      </span>
                    ) : null}
                  </div>
                  {f.motivo ? <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{f.motivo}</span> : null}
                  <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
                    {f.origem === "retorno" ? "do retorno da prefeitura" : f.origem === "presumido" ? "presumido no fechamento" : "registro manual"}
                    {f.parte ? ` · parte ${f.parte}` : ""} · {new Date(f.registradoEm).toLocaleString("pt-BR")}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ============================================================ Conciliação

/**
 * O fechamento de uma competência, número por número.
 *
 * Todos derivados do estado persistido. Dois deles vêm do resumo da ÚLTIMA
 * importação, não da posição atual — e a tela diz isso, porque apresentar número
 * de importação como se fosse estado é como um contador desatualizado ganha
 * autoridade.
 */
export function Conciliacao({ folhaId }: { folhaId: number }) {
  const [competencia, setCompetencia] = useState(competenciaAtual());

  const q = useQuery({
    queryKey: ["admin", "rubricas", "conciliacao", folhaId, competencia],
    queryFn: () => atlas.admin.rubricas.conciliacaoResumo(folhaId, competencia),
    enabled: /^\d{6}$/.test(competencia),
  });

  const r = q.data;

  /** Os números de POSIÇÃO — derivados do estado, sempre atuais. */
  const posicao: { rotulo: string; valor: number; dica: string; tom?: "alerta" | "bom" }[] = r ? [
    { rotulo: "Esperados", valor: r.esperados, dica: "Quantas dívidas a Atlas instruiu nesta competência." },
    { rotulo: "Realizados", valor: r.realizados, dica: "Quantos fatos o retorno da prefeitura gravou." },
    { rotulo: "Conciliados", valor: r.conciliados, dica: "Desconto integral: o valor instruído saiu inteiro.", tom: "bom" },
    { rotulo: "Desconto parcial", valor: r.parciais, dica: "A folha retirou menos que a parcela. NÃO quita — o resto fica em aberto.", tom: r.parciais > 0 ? "alerta" : undefined },
    { rotulo: "Não descontados", valor: r.naoDescontados, dica: "Houve folha e o desconto não saiu. A prefeitura deve explicação.", tom: r.naoDescontados > 0 ? "alerta" : undefined },
    { rotulo: "Sem folha no mês", valor: r.semFolha, dica: "O servidor não teve salário (licença, cessão). NÃO é falha da prefeitura." },
    { rotulo: "Suspensos pelo teto", valor: r.suspensosPorTeto ?? 0, dica: "A Atlas tirou do movimento para caber no teto global da regra do órgão. NÃO é falha da prefeitura." },
    { rotulo: "Sem retorno", valor: r.semRetorno, dica: "Instruído e sem fato nenhum: nem sucesso, nem falha declarada.", tom: r.semRetorno > 0 ? "alerta" : undefined },
    { rotulo: "Interrompidos", valor: r.interrompidos, dica: "Desligamento tirou o contrato da folha no meio do caminho." },
    { rotulo: "Inesperados", valor: r.inesperados, dica: "Desconto que a Atlas não instruiu, em quarentena esperando decisão.", tom: r.inesperados > 0 ? "alerta" : undefined },
    { rotulo: "Rubricas pendentes", valor: r.rubricasPendentes, dica: "Verbas sem classificação. O desconto delas não virou dívida de consignatária alguma.", tom: r.rubricasPendentes > 0 ? "alerta" : undefined },
  ] : [];

  /** Os números da ÚLTIMA IMPORTAÇÃO — não são posição, e o rótulo avisa. */
  const daImportacao: { rotulo: string; valor: number; dica: string }[] = r ? [
    { rotulo: "Duplicidades", valor: r.duplicidades, dica: "Mesma ADF reivindicada por duas partes do retorno. A primeira permanece; a segunda foi recusada." },
    { rotulo: "Ambiguidades", valor: r.ambiguidades, dica: "Linha que corresponde a mais de uma dívida de forma indistinguível. Não é aplicada a nenhuma — escolher seria amortizar a dívida errada." },
    { rotulo: "Divergência de valor", valor: r.divergenciasValor, dica: "O cronograma da folha discorda do contrato da Atlas. Reportado, nunca aplicado: a verdade do contrato originado aqui é a CCB." },
  ] : [];

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ minWidth: 200 }}>
          <TextField
            label="Competência"
            value={competencia}
            onChange={(e) => setCompetencia(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="202608"
            hint="AAAAMM"
          />
        </div>
        {r ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)", paddingBottom: 8 }}>
            {r.retornoProcessado
              ? "Retorno processado — os números abaixo são o fechamento desta folha."
              : "Nenhum retorno processado nesta competência: a folha ainda está aberta e os números são parciais."}
          </div>
        ) : null}
      </div>

      {q.isLoading ? (
        <div style={{ padding: 36, textAlign: "center", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 12 }}>
          Carregando…
        </div>
      ) : q.isError ? (
        <div style={{ padding: 14, borderRadius: 10, fontSize: 13, background: "color-mix(in srgb, var(--danger-500) 12%, transparent)" }}>
          Não foi possível carregar o resumo desta competência.
        </div>
      ) : r ? (
        <>
          <Secao titulo="Posição da competência" nota="Derivada do estado — sempre atual.">
            <GradeNumeros itens={posicao} />
          </Secao>

          <Secao
            titulo="Da última importação"
            nota={r.arquivoDeOrigem
              ? `Do arquivo ${r.arquivoDeOrigem.nome || "(sem nome)"}, recebido em ${new Date(r.arquivoDeOrigem.recebidoEm).toLocaleString("pt-BR")}${r.arquivoDeOrigem.parte ? ` · parte ${r.arquivoDeOrigem.parte}` : ""}.`
              : "Nenhum retorno importado nesta competência ainda."}
          >
            {/* A DISTINÇÃO IMPORTA: estes três não são posição, são o que a última
                importação encontrou. Misturá-los com os de cima daria a um número
                de um momento a autoridade de um número de agora. */}
            <GradeNumeros itens={daImportacao} />
          </Secao>
        </>
      ) : null}
    </div>
  );
}

function Secao({ titulo, nota, children }: { titulo: string; nota: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
          {titulo}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{nota}</div>
      </div>
      {children}
    </div>
  );
}

function GradeNumeros({ itens }: { itens: { rotulo: string; valor: number; dica: string; tom?: "alerta" | "bom" }[] }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
      {itens.map((i) => (
        <div
          key={i.rotulo}
          title={i.dica}
          style={{
            padding: 12, borderRadius: 10, background: "var(--bg-elev-2)",
            border: `1px solid ${i.tom === "alerta" ? "var(--gold-500)" : i.tom === "bom" ? "var(--emerald-500)" : "var(--border)"}`,
          }}
        >
          <div style={{ fontSize: 11, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: ".06em" }}>
            {i.rotulo}
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>{i.valor}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================ Histórico

/**
 * O que mudou no catálogo desta folha, quem mudou e quando.
 *
 * Lê a auditoria na categoria `margem` — a mesma trilha que já registrava
 * confirmação de rubrica, agora com `antes`/`depois` estruturados. Antes só
 * havia a frase em `detalhes`, que é boa de ler e inútil para responder "o que
 * estava gravado antes disto?" — e é essa a pergunta que se faz quando um
 * repasse foi para o banco errado.
 */
export function HistoricoRubricas() {
  const [acao, setAcao] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);

  const q = useQuery({
    queryKey: ["admin", "auditoria", "margem"],
    queryFn: () => atlas.admin.listAuditoria({ categoria: "margem", limit: 300 }),
  });

  const todas = q.data?.entries ?? [];
  const acoes = useMemo(() => [...new Set(todas.map((e) => e.acao))].sort(), [todas]);
  const filtradas = useMemo(
    () => todas.filter((e) => !acao || e.acao === acao),
    [todas, acao],
  );

  const inicio = (pagina - 1) * porPagina;
  const daPagina = filtradas.slice(inicio, inicio + porPagina);

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div style={{ padding: 12, borderRadius: 10, fontSize: 13, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
        Toda decisão de classificação fica registrada com quem a tomou. A trilha é global à averbadora — não
        filtra por folha, porque a auditoria é sobre o ato, não sobre o objeto.
      </div>

      {/* `searchValue=""` sem `onSearchChange` saiu: era uma caixa de busca
          morta — digitava e nada acontecia. O `FilterBar` agora so' renderiza a
          busca para quem escuta, entao esta aba fica com o filtro que ela de
          fato tem (Acao). Busca de verdade na trilha depende de decidir quais
          campos ela cobre, e isso vai no grupo do Motor de rubricas. */}
      <FilterBar
        onReset={() => { setAcao(""); setPagina(1); }}
      >
        <div style={GRADE_FILTROS}>
          <SelectField
            label="Ação" value={acao} onChange={(e) => { setAcao(e.target.value); setPagina(1); }}
            options={[{ value: "", label: "— TODAS —" }, ...acoes.map((a) => ({ value: a, label: a }))]}
          />
        </div>
      </FilterBar>

      {q.isLoading ? (
        <div style={{ padding: 36, textAlign: "center", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 12 }}>
          Carregando…
        </div>
      ) : daPagina.length === 0 ? (
        <div style={{ padding: 36, textAlign: "center", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: 12 }}>
          {/* Com `acao` ligado, "ainda nao ha registro" e' falso: ha registro,
              nenhum daquela acao. Afirmar que a trilha esta vazia numa tela de
              auditoria e' a leitura errada mais cara. */}
          {acao
            ? `Nenhum registro da ação "${acao}". Limpe o filtro para ver a trilha inteira.`
            : "Nenhum registro de classificação ainda."}
        </div>
      ) : (
        <div style={{ display: "grid", gap: 6 }}>
          {daPagina.map((e) => (
            <div
              key={e.id}
              style={{ padding: 10, borderRadius: 8, background: "var(--bg-elev-2)", border: "1px solid var(--border)", display: "grid", gap: 4 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <strong style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{e.acao}</strong>
                <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
                  {new Date(e.ts).toLocaleString("pt-BR")}
                </span>
              </div>
              <span style={{ fontSize: 13 }}>{e.detalhes}</span>
              {e.antes || e.depois ? <AntesDepois antes={e.antes} depois={e.depois} /> : null}
              <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
                por {e.userId ?? "—"}
              </span>
            </div>
          ))}
        </div>
      )}

      <Pagination
        total={filtradas.length}
        pagina={pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="registros"
      />
    </div>
  );
}

/**
 * Só os campos que MUDARAM, lado a lado.
 *
 * Mostrar o objeto inteiro nas duas colunas obrigaria quem lê a comparar campo a
 * campo — e a pergunta é sempre "o que mudou?", nunca "como era tudo?".
 */
function AntesDepois({ antes, depois }: { antes?: Record<string, unknown>; depois?: Record<string, unknown> }) {
  const chaves = [...new Set([...Object.keys(antes ?? {}), ...Object.keys(depois ?? {})])]
    .filter((k) => JSON.stringify(antes?.[k]) !== JSON.stringify(depois?.[k]));
  if (chaves.length === 0) return null;

  const mostrar = (v: unknown) => v == null || v === "" ? "—" : String(v);

  return (
    <div style={{ display: "grid", gap: 2, fontSize: 11, fontFamily: "var(--font-mono)" }}>
      {chaves.map((k) => (
        <div key={k} style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={{ color: "var(--text-dim)", minWidth: 110 }}>{k}</span>
          <span style={{ color: "var(--danger-500)" }}>{mostrar(antes?.[k])}</span>
          <span style={{ color: "var(--text-dim)" }}>→</span>
          <span style={{ color: "var(--emerald-500)" }}>{mostrar(depois?.[k])}</span>
        </div>
      ))}
    </div>
  );
}
